import { makeAutoObservable, runInAction } from "mobx";
import { newUuid } from "@/utils/uuid";
import {
  adminOverview,
  adminRoles,
  adminShareholderDetails,
  adminUsersSettings,
} from "@/utils/navigation";
import { RadioFieldModel, StringFieldModel } from "@/common/commonForms";
import { TMessage } from "@/i18n/tt";
import {
  resolveUrnReference,
  toCompanyRoleReference,
  toShareholderReference,
} from "@/utils/UrnReference";
import { ShareholderDTO } from "./Shareholder";
import { DCEvent, HttpEvent, httpGet, httpPost } from "@/events/eventsDomain";
import { PhoneNumber } from "@/common/common";
import { DbotEventDispatcher } from "@/dbot/common/commonDomain";
import { RoleType } from "./enums/RoleType";

export enum ActorType {
  SHAREHOLDER = "SHAREHOLDER",
  BOARD_MEMBER = "BOARD_MEMBER",
  CEO = "CEO",
  ADMIN = "ADMIN",
  ADMINISTERED_COMPANY = "ADMINISTERED_COMPANY",
  CUSTOM_ROLE = "CUSTOM_ROLE",
}

export const shareRecipientActorTabs = [
  ActorType.SHAREHOLDER,
  ActorType.ADMINISTERED_COMPANY,
  ActorType.BOARD_MEMBER,
  ActorType.CEO,
];

export type Actor = {
  type: ActorType;
  name: string;
  isCompany: boolean;
  companyNumber?: string;
  socialSecurityNumber?: string;
  birthDateOrOrganisationNumber?: string;
  nationality?: string;
  dNumber?: string;
  email?: string;
  phoneNumber?: PhoneNumber;
  availableActors: AvailableActor[];
  roleType?: RoleType;
  inCompany?: ActorInCompany;
};

export type ActorInCompany = {
  shareholderId?: number;
  companyRoleIds: number[];
};

export type AvailableActor = {
  reference: string;
  organisationNumber: string;
  companyName: string;
  representativeName?: string;
};

export type SelectedActor = {
  reference: string;
  name: string;
  isCompany: boolean;
  companyNumber?: string;
  socialSecurityNumber?: string;
  birthDateOrOrganisationNumber?: string;
  nationality?: string;
  dNumber?: string;
  email?: string;
  phoneNumber?: PhoneNumber;
  type: ActorType;
  organisationNumber: string;
  representativeName?: string;
  inCompany?: ActorInCompany;
};

export type ActorsSelectorModelParams = {
  title: TMessage;
  organisationNumber: string;
  tabs: ActorType[];
  excludeActors: string[];
  onLoad: (
    type: ActorType,
    onSuccess: (result: Actor[]) => DCEvent[],
    onFailure: () => DCEvent[],
  ) => void;
  onSubmit: (result: SelectedActor[], onError: () => void) => void;
  onClose: () => void;
  isMultiSelect?: boolean;
  excludeCompanies?: boolean;
  defaultCompanyFilterValue?: string | null;
};

export class ActorsSelectorModel {
  organisationNumber: string;
  actors: ActorModel[];
  tabs: ActorType[];
  selectedTab: ActorType;
  excludeActors: string[];
  excludeCompanies: boolean;

  title: TMessage;
  loadActors: (
    type: ActorType,
    onSuccess: (result: Actor[]) => DCEvent[],
    onFailure: () => DCEvent[],
  ) => void;
  onSubmit: (result: SelectedActor[], onError: () => void) => void;
  onClose: () => void;
  isMultiSelect: boolean;
  defaultCompanyFilterValue: string | null;

  searchFilter: StringFieldModel;
  companyFilter?: RadioFieldModel<string | null>;
  isLoading: boolean = false;

  constructor({
    title,
    organisationNumber,
    tabs,
    excludeActors,
    onLoad,
    onSubmit,
    onClose,
    isMultiSelect = true,
    excludeCompanies = false,
    defaultCompanyFilterValue,
  }: ActorsSelectorModelParams) {
    this.title = title;
    this.organisationNumber = organisationNumber;
    this.loadActors = onLoad;
    this.onSubmit = onSubmit;
    this.onClose = onClose;
    this.isMultiSelect = isMultiSelect;
    this.actors = [];

    this.tabs = tabs;
    this.selectedTab = tabs[0];
    this.excludeActors = excludeActors;
    this.excludeCompanies = excludeCompanies;
    this.defaultCompanyFilterValue =
      defaultCompanyFilterValue !== undefined ? defaultCompanyFilterValue : organisationNumber;

    this.searchFilter = new StringFieldModel();

    makeAutoObservable(this, {}, { autoBind: true });
  }

  fetchActors(type: ActorType = this.selectedTab) {
    if (this.actors.some((a) => a.type === type)) {
      return;
    }
    this.setLoading(true);
    this.loadActors(
      type,
      (result) => {
        const filteredResults = result
          .filter((actor) => this.excludeByCompanyNumber(actor))
          .filter((actor) => this.excludeById(actor))
          .filter((actor) => this.excludeByCompany(actor));

        this.addActors(filteredResults);
        this.initCompanyFilter();
        this.setLoading(false);
        return [];
      },
      () => {
        this.setLoading(false);
        return [];
      },
    );
  }

  addActors(actors: Actor[]) {
    this.actors.push(...actors.map((actor) => new ActorModel(actor)));
  }

  excludeByCompanyNumber(actor: Actor) {
    return !(actor.companyNumber !== undefined && this.excludeActors.includes(actor.companyNumber));
  }

  excludeById(actor: Actor) {
    return !actorReferences(actor).some((reference) => this.excludeActors.includes(reference));
  }

  excludeByCompany(actor: Actor) {
    return !(this.excludeCompanies && actor.isCompany);
  }

  get allCompanyOrgNos() {
    return [
      ...new Set(
        this.actorsByTab.flatMap((a) => a.availableActors.map((s) => s.organisationNumber)),
      ),
    ];
  }

  get allCompanies() {
    return [...new Set(this.actorsByTab.flatMap((a) => a.availableActors))];
  }

  findCompanyName(orgNo: string) {
    return this.allCompanies.find((a) => a.organisationNumber === orgNo)?.companyName ?? "";
  }

  get isOnCompaniesTab() {
    return this.selectedTab === ActorType.ADMINISTERED_COMPANY;
  }

  get isCompanyFilterVisible() {
    return (
      !this.isOnCompaniesTab && this.companyFilter !== undefined && this.allCompanyOrgNos.length > 1
    );
  }

  get companyFilterValue() {
    return this.isOnCompaniesTab ? undefined : this.companyFilter?.value;
  }

  initCompanyFilter() {
    if (this.isOnCompaniesTab) {
      return;
    }
    const value =
      this.companyFilter !== undefined ? this.companyFilter.value : this.defaultCompanyFilterValue;
    this.companyFilter = new RadioFieldModel<string | null>({
      value: this.allCompanyOrgNos.find((orgNo) => orgNo === value) ?? null,
      options: [null, ...this.allCompanyOrgNos],
      idFn: (c) => (c ? c : "all"),
      labelFn: (c) =>
        c ? { translated: this.findCompanyName(c) } : { key: "portfolio.all-companies" },
    });
  }

  getActorsByType(type: ActorType) {
    return this.actors.filter((actor) => actor.type === type);
  }

  get actorsByTab() {
    return this.getActorsByType(this.selectedTab);
  }

  setSelectedTab(tab: ActorType) {
    this.selectedTab = tab;
    if (this.actors.every((actor) => actor.type !== tab)) {
      this.fetchActors();
    } else {
      this.initCompanyFilter();
    }
  }

  setLoading(value: boolean) {
    this.isLoading = value;
  }

  selectActor(actor: ActorModel) {
    if (!this.isMultiSelect) {
      this.setLoading(true);
      this.onSubmit([actor.toSelectedActor()], () => this.setLoading(false));
    }
  }

  get selectedActors() {
    return this.actors.filter((actor) => actor.isSelected);
  }

  get filteredActors() {
    return this.actorsByTab.filter((s) =>
      s.matches(this.searchFilter.inputValue.toLowerCase(), this.companyFilterValue),
    );
  }

  get areAllFilteredSelected() {
    return this.filteredActors.length > 0 && this.filteredActors.every((a) => a.isSelected);
  }

  selectAllFiltered() {
    const selectValue = !this.areAllFilteredSelected;
    this.filteredActors.forEach((a) => a.toggleSelectedActor(selectValue));
  }

  submitActors(actors: ActorModel[] = this.selectedActors) {
    this.setLoading(true);
    this.onSubmit(
      actors.map((actor) => actor.toSelectedActor()),
      () => this.setLoading(false),
    );
  }
}

export class ActorModel {
  id: string;
  actor: Actor;
  type: ActorType;
  availableActors: AvailableActor[];
  selectedActor: AvailableActor;
  isSelected: boolean = false;

  constructor(actor: Actor) {
    this.actor = actor;
    this.id = newUuid();
    this.type = actor.type;
    this.availableActors = actor.availableActors;
    this.selectedActor = actor.availableActors[0];
    makeAutoObservable(this, {}, { autoBind: true });
  }

  setSelectedActor(actor: AvailableActor) {
    this.selectedActor = actor;
  }

  toggleSelectedActor(value?: boolean) {
    this.isSelected = value ?? !this.isSelected;
  }

  get goToUrl() {
    switch (this.type) {
      case ActorType.SHAREHOLDER: {
        const reference = resolveUrnReference(this.selectedActor.reference);
        return reference !== undefined && reference.type === "shareholderId"
          ? adminShareholderDetails(this.selectedActor.organisationNumber, reference.id)
          : undefined;
      }
      case ActorType.BOARD_MEMBER:
      case ActorType.CEO:
        return adminRoles(this.selectedActor.organisationNumber);
      case ActorType.ADMIN:
      case ActorType.CUSTOM_ROLE:
        return adminUsersSettings(this.selectedActor.organisationNumber);
      case ActorType.ADMINISTERED_COMPANY:
        return adminOverview(this.selectedActor.organisationNumber);
    }
  }

  matches(filterString: string, filterCompanyOrgNo?: string | null) {
    return (
      this.filterAvailableActors(filterCompanyOrgNo).length > 0 &&
      (this.actor.name.toLowerCase().includes(filterString) ||
        (this.selectedActor.representativeName !== undefined &&
          this.selectedActor.representativeName.toLowerCase().includes(filterString)) ||
        (this.actor.birthDateOrOrganisationNumber !== undefined &&
          this.actor.birthDateOrOrganisationNumber.includes(filterString)))
    );
  }

  filterAvailableActors(filterCompanyOrgNo?: string | null) {
    const availableActorsFiltered = filterCompanyOrgNo
      ? this.availableActors.filter((d) => d.organisationNumber === filterCompanyOrgNo)
      : this.availableActors;
    if (availableActorsFiltered.length === 1) {
      runInAction(() => {
        this.setSelectedActor(availableActorsFiltered[0]);
      });
    }
    return availableActorsFiltered;
  }

  toSelectedActor(): SelectedActor {
    return {
      reference: this.selectedActor.reference,
      name: this.actor.name,
      isCompany: this.actor.isCompany,
      companyNumber: this.actor.companyNumber,
      socialSecurityNumber: this.actor.socialSecurityNumber,
      birthDateOrOrganisationNumber: this.actor.birthDateOrOrganisationNumber,
      nationality: this.actor.nationality,
      dNumber: this.actor.dNumber,
      email: this.actor.email,
      phoneNumber: this.actor.phoneNumber,
      type: this.type,
      organisationNumber: this.selectedActor.organisationNumber,
      representativeName: this.selectedActor.representativeName,
      inCompany: this.actor.inCompany,
    };
  }
}

export function actorReferences(actor: Actor): string[] {
  return [
    ...actor.availableActors.map((a) => a.reference),
    ...(actor.inCompany?.shareholderId === undefined
      ? []
      : [toShareholderReference(actor.inCompany.shareholderId)]),
    ...(actor.inCompany?.companyRoleIds ?? []).map(toCompanyRoleReference),
  ];
}

export function actorTypeLabel(t: ActorType): TMessage | undefined {
  switch (t) {
    case ActorType.SHAREHOLDER:
      return { key: "actors.type.shareholder" };
    case ActorType.BOARD_MEMBER:
      return { key: "actors.type.board-members" };
    case ActorType.CEO:
      return { key: "actors.type.ceos" };
    case ActorType.ADMINISTERED_COMPANY:
      return { key: "actors.type.administration" };
    default:
      return undefined;
  }
}

export function loadActors(
  type: ActorType,
  onSuccess: (result: Actor[]) => DCEvent[],
  onFailure: () => DCEvent[],
  dispatch: DbotEventDispatcher,
  orgNo: string,
) {
  switch (type) {
    case ActorType.SHAREHOLDER:
      dispatch(actorsApi.getShareholders({ orgNo, onSuccess, onFailure }));
      break;
    case ActorType.ADMINISTERED_COMPANY:
    case ActorType.BOARD_MEMBER:
    case ActorType.CEO:
      dispatch(actorsApi.getCompanyRoles({ orgNo, type, onSuccess, onFailure }));
      break;
    default:
      onSuccess([]);
  }
}

export const actorsApi = {
  getShareholders: ({ orgNo, onSuccess, onFailure }: GetShareholderAsActorsRequest): HttpEvent =>
    httpGet({
      url: `/shareholder/${orgNo}/actors`,
      onSuccess: (res) => onSuccess(res.data),
      onFailure,
    }),

  getCompanyRoles: ({
    orgNo,
    type,
    onSuccess,
    onFailure,
  }: GetCompanyRolesAsActorsRequest): HttpEvent =>
    httpGet({
      url: `/company-roles/${orgNo}/actors/${type}`,
      onSuccess: (res) => onSuccess(res.data),
      onFailure,
    }),

  addShareholdersFromActors: ({
    orgNo,
    data,
    onSuccess,
    onFailure,
  }: AddShareholdersFromActorsRequest): HttpEvent =>
    httpPost({
      url: `/shareholder/${orgNo}/actors`,
      data,
      onSuccess: (res) => onSuccess(res.data),
      onFailure,
    }),
};

type GetShareholderAsActorsRequest = {
  orgNo: string;
  onSuccess: (res: Actor[]) => DCEvent[];
  onFailure: () => DCEvent[];
};

type GetCompanyRolesAsActorsRequest = {
  orgNo: string;
  type: ActorType;
  onSuccess: (res: Actor[]) => DCEvent[];
  onFailure: () => DCEvent[];
};

type AddShareholdersFromActorsRequest = {
  orgNo: string;
  data: SelectedActor[];
  onSuccess: (res: ShareholderDTO[]) => DCEvent[];
  onFailure: () => DCEvent[];
};
